/**
 * MAP Admin Info Service
 *
 * Fetches administrative unit metadata from the MAP data API,
 * providing country-to-ISO3 code mappings used for generating
 * survey identifiers (mapSurveyId).
 */

/** Shape of a single ADMIN0 entry from the MAP data API. */
interface Admin0Entry {
  id: string
  countryName: string
  name: string
  iso3: string
  iso2: string
  admin1Name: string
}

/** The MAP data API endpoint for ADMIN0 metadata (includes non-endemic countries). */
const ADMIN0_ENDPOINT =
  "https://data.malariaatlas.org/admin-unit-metadata/admin-units/ADMIN0?includeNonEndemic=true"

/**
 * Fetch the ADMIN0 metadata and build a Map from countryName → ISO3 code.
 *
 * This map is used to derive the mapSurveyId when creating a new survey
 * (mapSurveyId = iso3 + year + surveyType).
 *
 * @returns A Map where keys are country names and values are ISO3 codes.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function getCountryToIso3Map(): Promise<Map<string, string>> {
  const response = await fetch(ADMIN0_ENDPOINT)

  if (!response.ok) {
    throw new Error(
      `Failed to fetch ADMIN0 metadata: HTTP ${response.status}`
    )
  }

  const entries: Admin0Entry[] = await response.json()

  const countryToIso3 = new Map<string, string>()
  for (const entry of entries) {
    // Use countryName as the key since that's what surveys reference
    countryToIso3.set(entry.countryName, entry.iso3)
  }

  return countryToIso3
}

/**
 * Fetch the ADMIN0 metadata and build a Map from ISO3 code → countryName.
 *
 * This is the reverse of {@link getCountryToIso3Map}. It is used to translate
 * the ISO3 codes stored on routine surveillance rows into human-readable
 * country names for display (e.g. in the country dropdown filter).
 *
 * Multiple ADMIN0 entries may share the same ISO3 code (one per admin1
 * subdivision), but they carry the same countryName, so collapsing them into a
 * single ISO3 → countryName entry is safe. The first non-empty countryName seen
 * for an ISO3 wins; later duplicates are ignored.
 *
 * @returns A Map where keys are ISO3 codes and values are country names.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function getIso3ToCountryMap(): Promise<Map<string, string>> {
  const response = await fetch(ADMIN0_ENDPOINT)

  if (!response.ok) {
    throw new Error(
      `Failed to fetch ADMIN0 metadata: HTTP ${response.status}`
    )
  }

  const entries: Admin0Entry[] = await response.json()

  const iso3ToCountry = new Map<string, string>()
  for (const entry of entries) {
    // Only set once per ISO3 so the first (canonical) country name wins and we
    // don't churn the value with duplicate admin1 rows sharing the same ISO3.
    if (entry.iso3 && entry.countryName && !iso3ToCountry.has(entry.iso3)) {
      iso3ToCountry.set(entry.iso3, entry.countryName)
    }
  }

  return iso3ToCountry
}

/**
 * Fetch the ADMIN0 metadata and build a Map from ISO3 code → ADMIN0 unit id.
 *
 * @returns A Map where keys are ISO3 codes and values are ADMIN0 unit ids.
 * @throws Error if the API call fails or returns a non-OK status.
 */
export async function getIso3ToAdmin0IdMap(): Promise<Map<string, string>> {
  const response = await fetch(ADMIN0_ENDPOINT)

  if (!response.ok) {
    throw new Error(
      `Failed to fetch ADMIN0 metadata: HTTP ${response.status}`
    )
  }

  const entries: Admin0Entry[] = await response.json()

  const iso3ToAdmin0Id = new Map<string, string>()
  for (const entry of entries) {
    if (entry.iso3 && entry.id && !iso3ToAdmin0Id.has(entry.iso3)) {
      iso3ToAdmin0Id.set(entry.iso3, entry.id)
    }
  }

  return iso3ToAdmin0Id
}
